import { useFormContext } from 'react-hook-form';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import type { AddWizardValues } from '../../schemas';
import { usePositionsCatalog, useUnitsCatalog } from '../../hooks';
import styles from './StepConfirm.module.css';

export interface StepConfirmProps {
  formError?: string | null;
}

export function StepConfirm({ formError = null }: StepConfirmProps) {
  const { watch } = useFormContext<AddWizardValues>();
  const v = watch();
  const positions = usePositionsCatalog();
  const units = useUnitsCatalog();

  // Preview reflects the user's picks from StepIdentity (positionId/unitId),
  // not the Staff Profile defaults — those defaults were pre-loaded into the
  // form fields already, so the form is the source of truth here.
  const position = v.positionId
    ? positions.data?.items.find((p) => p.id === v.positionId)
    : undefined;
  const unit = v.unitId ? units.data?.items.find((u) => u.id === v.unitId) : undefined;

  const displayName = [v.first_name, v.last_name].filter(Boolean).join(' ') || t('New Member');

  return (
    <section data-testid="step-confirm">
      <div className={styles.header}>
        <div className={styles.stepLabel}>{t('STEP 5')}</div>
        <h1 className={styles.title}>
          {t('Ready to add {name}?', { name: v.first_name || t('them') })}
        </h1>
        <p className={styles.subtitle}>
          {t("Here's how they'll show up in your team. You can edit any of this later.")}
        </p>
      </div>

      <div className={styles.previewCard}>
        <Avatar name={displayName} size="lg" kind="ai" />
        <div className={styles.previewBody}>
          <div className={styles.previewNameRow}>
            <span className={styles.previewName}>{displayName}</span>
            <Chip variant="neutral" size="sm">
              {t('AI')}
            </Chip>
          </div>
          {position || unit ? (
            <div className={styles.previewAssignments}>
              {unit ? (
                <span className={styles.assignment}>
                  <span className={styles.assignmentValue}>{unit.name}</span>
                </span>
              ) : null}
              {position ? (
                <span className={styles.assignment}>
                  <span className={styles.assignmentValue}>{position.name}</span>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.briefStack}>
        <div className={styles.briefPanel}>
          <div className={styles.briefLabel}>{t('HOW THEY WORK')}</div>
          <div className={styles.briefBody}>{v.personality}</div>
        </div>
        <div className={styles.briefPanel}>
          <div className={styles.briefLabel}>{t('INSTRUCTIONS')}</div>
          <div className={styles.briefBody}>{v.systemPrompt}</div>
        </div>
      </div>

      {formError ? (
        <div className={styles.formError} role="alert">
          {formError}
        </div>
      ) : null}
    </section>
  );
}
